import {
  AbstractControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ValidatorMessageModel } from '../validator-message.model';

export class CustomValidators {
  static required(control: AbstractControl): {
    [key: string]: ValidatorMessageModel;
  } {
    const valid = Validators.required(control);
    return valid
      ? { required: new ValidatorMessageModel('validators.required') }
      : null!;
  }

  static requiredTrue(control: AbstractControl): {
    [key: string]: ValidatorMessageModel;
  } {
    const valid = Validators.requiredTrue(control);
    return valid
      ? { required: new ValidatorMessageModel('validators.requiredTrue') }
      : null!;
  }

  static oneRequired(control: AbstractControl): {
    [key: string]: ValidatorMessageModel;
  } {
    const valid = Validators.required(control);
    return valid
      ? { required: new ValidatorMessageModel('validators.oneRequired') }
      : null!;
  }

  static noNumbers(control: AbstractControl): {
    [key: string]: ValidatorMessageModel;
  } {
    const format = /^([^0-9]*)$/;
    const valid = !format.test(control.value);
    return valid
      ? {
          noNumbers: new ValidatorMessageModel('validators.noNumbers'),
        }
      : null!;
  }

  static justNumbers(control: AbstractControl): {
    [key: string]: ValidatorMessageModel;
  } {
    const format = /^[0-9]*$/;
    const valid = !format.test(control.value);
    return valid
      ? {
          noNumbers: new ValidatorMessageModel('validators.justNumbers'),
        }
      : null!;
  }

  static ibanValidator(control: AbstractControl): {
    [key: string]: ValidatorMessageModel;
  } {
    if (!control.value) {
      return null!;
    }
    const format =
      /^([A-Z]{2}[ \-]?[0-9]{2})(?=(?:[ \-]?[A-Z0-9]){9,30}$)((?:[ \-]?[A-Z0-9]{3,5}){2,7})([ \-]?[A-Z0-9]{1,3})?$/;
    const valid = !format.test(control.value);
    return valid
      ? {
          noNumbers: new ValidatorMessageModel('validators.invalidIban'),
        }
      : null!;
  }

  static email(control: AbstractControl): {
    [key: string]: ValidatorMessageModel;
  } {
    const valid = Validators.email(control);
    return valid
      ? { email: new ValidatorMessageModel('validators.email') }
      : null!;
  }

  static minLength(min: number): ValidatorFn {
    return (control: AbstractControl) => {
      if (Validators.minLength(min)(control)) {
        return {
          minLength: new ValidatorMessageModel(
            'validators.minLength',
            min.toString()
          ),
        };
      }
      return null;
    };
  }

  static maxLength(max: number): ValidatorFn {
    return (control: AbstractControl) => {
      if (Validators.maxLength(max)(control)) {
        return {
          maxLength: new ValidatorMessageModel(
            'validators.maxLength',
            max.toString()
          ),
        };
      }
      return null;
    };
  }

  static minValue(min: number): ValidatorFn {
    return (control: AbstractControl) => {
      if (Validators.min(min)(control)) {
        return {
          minValue: new ValidatorMessageModel(
            'validators.minValue',
            min.toString()
          ),
        };
      }
      return null;
    };
  }

  static atLeastOneUppercaseLetter(control: AbstractControl): {
    [key: string]: ValidatorMessageModel;
  } {
    const format = /^(?=.*[A-Z])/;
    const valid = !format.test(control.value);
    return valid
      ? {
          atLeastOneUppercaseLetter: new ValidatorMessageModel(
            'validators.atLeastOneUppercaseLetter'
          ),
        }
      : null!;
  }

  static atLeastOneLowercaseLetter(control: AbstractControl): {
    [key: string]: ValidatorMessageModel;
  } {
    const format = /^(?=.*[a-z])/;
    const valid = !format.test(control.value);
    return valid
      ? {
          atLeastOneLowercaseLetter: new ValidatorMessageModel(
            'validators.atLeastOneLowercaseLetter'
          ),
        }
      : null!;
  }

  static atLeastOneNumber(control: AbstractControl): {
    [key: string]: ValidatorMessageModel;
  } {
    const format = /^(?=.*\d)/;
    const valid = !format.test(control.value);
    return valid
      ? {
          atLeastOneNumber: new ValidatorMessageModel(
            'validators.atLeastOneNumber'
          ),
        }
      : null!;
  }

  static atLeastOneSpecialCharacter(control: AbstractControl): {
    [key: string]: ValidatorMessageModel;
  } {
    const format = /^(?=.*[!@#$%^&*?])/;
    const valid = !format.test(control.value);
    return valid
      ? {
          atLeastOneSpecialCharacter: new ValidatorMessageModel(
            'validators.atLeastOneSpecialCharacter'
          ),
        }
      : null!;
  }

  static maxValue(max: number): ValidatorFn {
    return (control: AbstractControl) => {
      if (Validators.max(max)(control)) {
        return {
          maxValue: new ValidatorMessageModel(
            'validators.maxValue',
            max.toString()
          ),
        };
      }
      return null;
    };
  }

  static atLeastOne(keys: string[], message: string) {
    return (group: FormGroup) => {
      const { controls } = group;
      return keys.some((key) => controls[key] && !!controls[key].value)
        ? null
        : {
            atLeastOne: new ValidatorMessageModel(message),
          };
    };
  }

  static pattern(pattern: string | RegExp): ValidatorFn {
    return (control: AbstractControl) => {
      if (Validators.pattern(pattern)(control)) {
        return { pattern: new ValidatorMessageModel('validators.pattern') };
      }
      return null;
    };
  }

  static passwordMatch(matchPasswordKey: string): ValidatorFn {
    return (control: AbstractControl) => {
      const matchPasswordControl = (<FormGroup>control.parent).get(
        matchPasswordKey
      );

      if (
        matchPasswordControl != null &&
        !matchPasswordControl.errors &&
        control.value != matchPasswordControl.value
      ) {
        return {
          passwordsMatch: new ValidatorMessageModel(
            'validators.passwordsMatch'
          ),
        };
      }
      return null;
    };
  }

  static atLeastOneRequired(
    keys: Array<string>,
    ignoredValues: Partial<Record<string, unknown[]>> = {}
  ): ValidatorFn {
    return (control: AbstractControl) => {
      let valid = false;

      keys.forEach((key) => {
        const value = (<FormGroup>control.parent).get(key)?.value;
        if (value && !ignoredValues[key]?.includes(value)) {
          valid = true;
          return;
        }
      });

      if (!valid) {
        return {
          atLeastOneRequired: new ValidatorMessageModel(
            'validators.atLeastOneRequired'
          ),
        };
      }
      return null;
    };
  }
}
